import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkCamera from 'vtk.js/Sources/Rendering/Core/Camera';
import vtkMapper from 'vtk.js/Sources/Rendering/Core/Mapper';
import vtkPolyData from 'vtk.js/Sources/Common/DataModel/PolyData';
import vtkTexture from 'vtk.js/Sources/Rendering/Core/Texture';
import vtkProperty from 'vtk.js/Sources/Rendering/Core/Property';

import vtkSTLReader from 'vtk.js/Sources/IO/Geometry/STLReader';
import vtkPLYReader from 'vtk.js/Sources/IO/Geometry/PLYReader';
import vtkXMLPolyDataReader from 'vtk.js/Sources/IO/XML/XMLPolyDataReader';
import vtkOrientationMarkerWidget from 'vtk.js/Sources/Interaction/Widgets/OrientationMarkerWidget';
import vtkAnnotatedCubeActor from 'vtk.js/Sources/Rendering/Core/AnnotatedCubeActor';
import vtkRenderWindowInteractor from 'vtk.js/Sources/Rendering/Core/RenderWindowInteractor';
import vtkTubeFilter from 'vtk.js/Sources/Filters/General/TubeFilter';
import vtkPlaneSource from 'vtk.js/Sources/Filters/Sources/PlaneSource';
import vtkRenderer from 'vtk.js/Sources/Rendering/Core/Renderer';
import j2xParser from 'fast-xml-parser';
import { mat4 } from 'gl-matrix';
import vtkMatrixBuilder from 'vtk.js/Sources/Common/Core/MatrixBuilder';
import { imageResizer } from '../model/ImageResizer';

import { arrayBufferToString, stringToArrayBuffer, typedArrayToBuffer } from './utils';
import { IMeshNode, MeshNode } from '@csdental/dicom-reader';
import { ViewOrientation } from './common';
import vtkMath from 'vtk.js/Sources/Common/Core/Math';


const MAX_TEXTURE_SIZE = 8192;

function readArrayBuffer(buffer: ArrayBuffer, name: string, format: string) {
  const mesh = new MeshNode('', format, name, '', new Uint8Array(buffer));
  return (mesh as any) as IMeshNode;
}

function readVTKXML(arraybuffer: ArrayBuffer) {
  const str = arrayBufferToString(arraybuffer);
  const xml = j2xParser.parse(str);
  const data3d = xml?.object3D?.data3D;
  const vtkFileStr = data3d?.replaceAll('&lt;', '<').replaceAll('&gt;', '>');
  return stringToArrayBuffer(vtkFileStr);
}

function createPolyData(buffer: Uint8Array, bufferType: string) {
  console.log('createPolyData: the buffer ', buffer ? 'length is ' + buffer.length : 'is null');
  if (buffer == null) {
    return null;
  }

  const arrayBuffer = typedArrayToBuffer(buffer);

  switch (bufferType) {
    case 'STL_FILE': {
      console.log('loading stl');
      const reader = vtkSTLReader.newInstance();
      reader.parseAsArrayBuffer(arrayBuffer);
      reader.update();
      return reader.getOutputPort();
    }

    case 'PLY_FILE':
    case 'PLY_TEXTURED_FILE': {
      console.log('loading ply');
      const reader = vtkPLYReader.newInstance();
      reader.parseAsArrayBuffer(arrayBuffer);
      reader.update();
      return reader.getOutputPort();
    }

    case 'VTK_FILE': {
      console.log('loading vtk');
      const vtkFileBuffer = readVTKXML(buffer);
      const reader = vtkXMLPolyDataReader.newInstance();
      reader.parseAsArrayBuffer(vtkFileBuffer);
      reader.update();
      return reader.getOutputPort();
    }

    default:
      return null;
  }
}

function loadImage(src: string) {
  return new Promise((resolve, reject) => {
    let img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function buildActor(mesh: IMeshNode): vtkActor {
  const polydata_port = createPolyData(mesh.meshBuffer, mesh.meshBufferType);
  if (polydata_port == null) {
    return null;
  }

  const mapper = vtkMapper.newInstance();
  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  mapper.setInputConnection(polydata_port);
  return actor;
}

function buildTexture(mesh: IMeshNode): vtkTexture {
  if (mesh.texture != null) {
    const texture = vtkTexture.newInstance();
    texture.setInterpolate(true);

    loadImage(mesh.texture).then((img) => {
      if (!img) {
        return;
      }
      const imgElement = img as HTMLIFrameElement;
      const w = Number(imgElement.width);
      const h = Number(imgElement.height);
      if (img && w < MAX_TEXTURE_SIZE && h < MAX_TEXTURE_SIZE) {
        texture.setImage(img);
        window.dispatchEvent(new Event('refreshCanvas'));
      } else {
        console.warn('texture is too large', imgElement.width, imgElement.height);
        vtkUtil.resizeImageToFitMaxTextureSize(imgElement, (img) => {
          texture.setImage(img);
          // (xinyu,20210331)the timeout is a workaround for texture not refresh issue
          setTimeout(() => {
            window.dispatchEvent(new Event('refreshCanvas'));
          }, 100);
        });
      }
    });
    return texture;
  } else {
    return null;
  }
}

function createMeshActor(mesh: IMeshNode): [vtkActor, vtkTexture] {
  const actor = buildActor(mesh);
  if (!actor) {
    return [null, null];
  }

  const texture = buildTexture(mesh);
  if (texture) {
    actor.addTexture(texture);
    setActorBackface(actor, true);
  }

  return [actor, texture];
}

function createPolyDataActor(polydata: vtkPolyData): vtkActor {
  const mapper = vtkMapper.newInstance();
  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  mapper.setInputData(polydata);
  return actor;
}

function setActorBackface(actor: vtkActor, enabled: boolean) {
  if (enabled) {
    if (actor.getBackfaceProperty()) {
      actor.getBackfaceProperty().setDiffuse(0.48);
    } else {
      const property = vtkProperty.newInstance();
      property.setDiffuse(0.48);
      actor.setBackfaceProperty(property);
    }
  } else {
    actor.setBackfaceProperty(null);
  }
}

class vtkUtil {
  static setCameraOrientation(camera: vtkCamera, viewOrientation: ViewOrientation) {
    camera.setPosition(0, 0, 0);
    switch (viewOrientation) {
      case ViewOrientation.Right: // Positive X
        camera.setFocalPoint(1, 0, 0);
        camera.setViewUp(0, 0, 1);
        break;
      case ViewOrientation.Left: // Negative X
        camera.setFocalPoint(-1, 0, 0);
        camera.setViewUp(0, 0, 1);
        break;
      case ViewOrientation.Front: // Postive Y
        camera.setFocalPoint(0, 1, 0);
        camera.setViewUp(0, 0, 1);
        break;
      case ViewOrientation.Back: // Negative Y
        camera.setFocalPoint(0, -1, 0);
        camera.setViewUp(0, 0, 1);
        break;
      case ViewOrientation.Top: // Postive Z
        camera.setFocalPoint(0, 0, 1);
        camera.setViewUp(0, -1, 0);
        break;
      case ViewOrientation.Bottom: // Negative Z
        camera.setFocalPoint(0, 0, -1);
        camera.setViewUp(0, 1, 0);
        break;
    }
  }

  static createOrientationMarkerWidget(interactor: vtkRenderWindowInteractor) {
    const axes = vtkAnnotatedCubeActor.newInstance();
    axes.setDefaultStyle({
      text: '+X',
      fontStyle: 'bold',
      fontFamily: 'Roboto',
      fontColor: 'white',
      fontSizeScale: (res) => res * 0.72,
      faceColor: '#E0BD00',
      faceRotation: 0,
      resolution: 800,
      edgeThickness: 0.06,
      edgeColor: 'white',
    });
    axes.setXPlusFaceProperty({
      text: 'L',
      faceRotation: 90,
    });
    axes.setXMinusFaceProperty({
      text: 'R',
      faceRotation: -90,
    });
    axes.setYPlusFaceProperty({
      text: 'P',
      faceRotation: 180,
    });
    axes.setYMinusFaceProperty({
      text: 'A',
    });
    axes.setZPlusFaceProperty({
      text: 'F',
      faceRotation: 0,
    });
    axes.setZMinusFaceProperty({
      text: 'H',
      faceRotation: 0,
    });
    // create orientation widget
    const wgt = vtkOrientationMarkerWidget.newInstance({
      actor: axes,
      interactor: interactor,
    });
    wgt.setEnabled(true);
    wgt.setViewportCorner(vtkOrientationMarkerWidget.Corners.BOTTOM_LEFT);
    wgt.setViewportSize(0.1);
    wgt.setMinPixelSize(100);
    wgt.setMaxPixelSize(300);
    return wgt;
  }

  static getPolyData(actor: vtkActor) {
    if (actor && actor.getMapper()) {
      return actor.getMapper().getInputData();
    }
    return null;
  }

  static buildActorFromDeepCopy(srcPolyData: vtkPolyData) {
    if (!srcPolyData) {
      return null;
    }
    const pointValues = srcPolyData.getPoints().getData();
    const cellValues = srcPolyData.getPolys().getData();
    const polyData = vtkPolyData.newInstance();
    polyData.getPoints().setData(pointValues, 3);
    polyData.getPolys().setData(cellValues);
    const mapper = vtkMapper.newInstance();
    const actor = vtkActor.newInstance();
    actor.setMapper(mapper);
    mapper.setInputData(polyData);
    return actor;
  }

  static placeOcclusionPlane(renderer: vtkRenderer, planeActor: vtkActor) {
    if (!renderer || !planeActor) {
      return;
    }
    const bds = renderer.computeVisiblePropBounds();
    const ratio = 1.2;
    const clr = [0.2, 0.2, 1.0];
    const opacity = 0.5;

    const xmin = bds[0] * ratio;
    const xmax = bds[1] * ratio;
    const ymin = bds[2] * ratio;
    const ymax = bds[3] * ratio;

    const plane = vtkPlaneSource.newInstance();
    plane.setOrigin(xmin, ymin, 0.0);
    plane.setPoint1(xmin, ymax, 0.0);
    plane.setPoint2(xmax, ymin, 0.0);
    plane.update();

    const mapper = vtkMapper.newInstance();
    mapper.setInputConnection(plane.getOutputPort());
    planeActor.setMapper(mapper);

    const prop = planeActor.getProperty();
    prop.setColor(clr);
    prop.setOpacity(opacity);
    renderer.addActor(planeActor);
  }

  static applyPatientOrientation(polyData: vtkPolyData, orientation: number[]) {
    const pts = polyData?.getPoints()?.getData();
    if (!pts || orientation?.length !== 16) {
      return;
    }
    const mat = mat4.fromValues(
      orientation[0],
      orientation[1],
      orientation[2],
      orientation[3],
      orientation[4],
      orientation[5],
      orientation[6],
      orientation[7],
      orientation[8],
      orientation[9],
      orientation[10],
      orientation[11],
      orientation[12],
      orientation[13],
      orientation[14],
      orientation[15]
    );
    mat4.transpose(mat, mat);
    const transform = vtkMatrixBuilder.buildFromDegree();
    transform.setMatrix(mat).apply(pts);
  }

  static resizeImageToFitMaxTextureSize(img: HTMLIFrameElement, onSuccess: (img: HTMLIFrameElement) => void) {
    console.time('resizeImageToFitMaxTextureSize');
    const sz = this.getMaxTextureSize(Number(img.width), Number(img.height));
    console.log('resizeImageToFitMaxTextureSize: w=' + sz.width + ',h=' + sz.height);
    imageResizer.resize(img?.src, sz.width, sz.height, (imgSrc: any) => {
      img.src = imgSrc;
      onSuccess?.(img);
      console.timeEnd('resizeImageToFitMaxTextureSize');
    });
  }

  static getMaxTextureSize(w: number, h: number) {
    const max = MAX_TEXTURE_SIZE;
    if (w <= max && h <= max) {
      return { width: w, height: h };
    }
    let wx = max;
    let hx = max;
    if (w > max) {
      hx = (max / w) * h;
      if (hx > max) {
        hx = max;
        wx = (max / h) * w;
      }
    } else {
      wx = (max / h) * w;
      if (wx > max) {
        wx = max;
        hx = (max / w) * h;
      }
    }
    return { width: wx, height: hx };
  }
}

function pointsToPolygon(pt3ds: Array<Array<number>>) {
  const polygon = vtkPolyData.newInstance();
  // make ploygon occlusive
  pt3ds.push(pt3ds[0]);
  const pointCoords = pt3ds.flat();
  const sz = pt3ds.length;
  const lineIndex = [sz, ...Array(sz).keys(), 0];
  polygon.getPoints().setData(Float32Array.from(pointCoords));
  polygon.getLines().setData(Uint16Array.from(lineIndex));
  return polygon;
}

function createTubeFilter(polydata: vtkPolyData) {
  const mapper = vtkMapper.newInstance();
  const actor = vtkActor.newInstance();
  actor.setMapper(mapper);
  const filter = vtkTubeFilter.newInstance();
  if (!filter) {
    mapper.setInputData(polydata);
  } else {
    filter.setCapping(false);
    filter.setNumberOfSides(3);
    filter.setRadius(0.1);
    filter.setInputData(polydata);
    mapper.setInputConnection(filter.getOutputPort());
  }
  return actor;
}


function rotateMatrix(matrix:mat4, rotation:number[]):mat4{
    const m = mat4.create();
    mat4.rotateX(m, m, vtkMath.radiansFromDegrees(rotation[0]));
    mat4.rotateY(m, m, vtkMath.radiansFromDegrees(rotation[1]));
    mat4.rotateZ(m, m, vtkMath.radiansFromDegrees(rotation[2]));
    return m;
}

export {
  createPolyData,
  createMeshActor,
  createPolyDataActor,
  setActorBackface,
  readArrayBuffer,
  pointsToPolygon,
  createTubeFilter,
  rotateMatrix,
  vtkUtil,
};
